/**
 * Daily summary computation — web mirror of the mobile DiaryContext summary
 * memo. Totals, percentages, performance, and micros must match the app so
 * the shared dailySummaries docs agree across platforms.
 */
import { sumMicros } from '@/lib/micros'
import { getKetoDailyTargets } from '@/keto/rules'
import type {
  DailyKetoTargets,
  DailySummary,
  DayPerformance,
  DiaryEntry,
  MealCounts,
  UserProfile,
} from '@/types'

export function computeTargets(profile?: UserProfile | null): DailyKetoTargets {
  return getKetoDailyTargets(profile)
}

export function computeSummary(
  date: string,
  entries: DiaryEntry[],
  profile?: UserProfile | null,
): DailySummary {
  const targets = computeTargets(profile)

  const baseTotals = entries.reduce(
    (acc, entry) => ({
      carbsG: acc.carbsG + (entry.nutrients.carbsG || 0),
      fiberG: acc.fiberG + (entry.nutrients.fiberG || 0),
      sugarG: acc.sugarG + (entry.nutrients.sugarG || 0),
      calories: acc.calories + (entry.nutrients.calories || 0),
      proteinG: acc.proteinG + (entry.nutrients.proteinG || 0),
      fatG: acc.fatG + (entry.nutrients.fatG || 0),
    }),
    { carbsG: 0, fiberG: 0, sugarG: 0, calories: 0, proteinG: 0, fatG: 0 },
  )

  // Total net carbs: prefer the stored per-entry netCarbsG (accounts for
  // sugar alcohols); fall back to carbs − fiber for entries lacking it.
  const netCarbsG = entries.reduce((acc, entry) => {
    const net =
      entry.nutrients.netCarbsG ??
      Math.max(0, (entry.nutrients.carbsG || 0) - (entry.nutrients.fiberG || 0))
    return acc + Math.max(0, net)
  }, 0)

  const totals = {
    ...baseTotals,
    netCarbsG: Math.round(netCarbsG * 10) / 10,
  }

  const micros = sumMicros(entries.map((e) => e.nutrients))

  const percentages: Record<string, number | undefined> = {
    netCarbs: targets.netCarbsG > 0 ? (totals.netCarbsG / targets.netCarbsG) * 100 : 0,
    fiber: targets.fiberG > 0 ? (totals.fiberG / targets.fiberG) * 100 : 0,
    sugar: targets.sugarG > 0 ? (totals.sugarG / targets.sugarG) * 100 : 0,
    calories:
      targets.calories && targets.calories > 0
        ? (totals.calories / targets.calories) * 100
        : undefined,
    protein:
      targets.proteinG && targets.proteinG > 0
        ? (totals.proteinG / targets.proteinG) * 100
        : undefined,
    fat: targets.fatG && targets.fatG > 0 ? (totals.fatG / targets.fatG) * 100 : undefined,
  }

  // Fiber is inverse: 100%+ means target MET (good)
  const fiberTargetMet = (percentages.fiber ?? 0) >= 100

  const overTargetNutrients: string[] = []
  if ((percentages.netCarbs ?? 0) > 100) overTargetNutrients.push('netCarbs')
  if ((percentages.sugar ?? 0) > 100) overTargetNutrients.push('sugar')

  // Performance rules (mobile parity — GL mirrors the net-carb budget):
  // green = under budget AND fiber met; red = >120% of the budget; else yellow.
  const performance: DayPerformance =
    entries.length === 0
      ? 'none'
      : overTargetNutrients.length === 0 && fiberTargetMet
        ? 'green'
        : (percentages.netCarbs ?? 0) > 120
          ? 'red'
          : 'yellow'

  const mealCounts: MealCounts = entries.reduce(
    (acc, entry) => {
      if (entry.mealType && entry.mealType in acc) {
        acc[entry.mealType as keyof MealCounts] += 1
      }
      return acc
    },
    { breakfast: 0, lunch: 0, dinner: 0, snack: 0 },
  )

  return {
    date,
    totals,
    micros,
    targets,
    percentages,
    mealCounts,
    performance,
    fiberTargetMet,
    hasOverTarget: overTargetNutrients.length > 0,
    overTargetNutrients,
  }
}
